import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * Writes a header size variable on `body`, skipping unchanged values because any write there
 * restyles the whole page. layout/theme.liquid sets the initial values.
 * @param {string} name
 * @param {number} height
 */
function setHeaderVariable(name, height) {
  const value = `${height}px`;
  if (document.body.style.getPropertyValue(name) !== value) document.body.style.setProperty(name, value);
}

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for the hiding animation and the reveal-to-top "settling" animation
   * (see [data-settling] below), when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for the hiding animation and the reveal-to-top "settling" animation,
   * when sticky behavior is 'scroll-up'. Must match the opacity/transform transition duration
   * on .header[data-sticky-state] and its .header__row, and the `top` transition on
   * .header[transparent], in sections/header.liquid (currently 0.32s) - this is what keeps
   * `position: sticky` in place for as long as either animation is actually playing. If this is
   * shorter than that CSS duration, .header-section drops back to `position: relative` (see the
   * `:has(#header-component[...][data-sticky-state='active'])` rule) while the header is still
   * visibly fading/sliding (hide) or easing back to its resting spacing/color (settle), which
   * reads as a jump/snap rather than a smooth transition.
   * @constant {number}
   */
  #animationDelay = 320;

  /**
   * Minimum scroll distance (px) required before re-evaluating scroll direction, when sticky
   * behavior is 'scroll-up'. Trackpad/touch momentum scrolling can produce tiny alternating
   * deltas within a single gesture; without a dead-zone those flip the direction every frame
   * and restart the reveal/hide animation repeatedly, which reads as the header flickering.
   * @constant {number}
   */
  #scrollDirectionThreshold = 8;

  /**
   * Coalesces #handleWindowScroll's work to at most once per animation
   * frame, instead of running its synchronous getBoundingClientRect() reads
   * on every 'scroll' event.
   * @type {boolean}
   */
  #scrollTicking = false;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    setHeaderVariable('--header-height', height);

    // `--header-group-height` (consumed by e.g. the shop mega menu's `top` offset) is otherwise
    // only recalculated when a *sibling* in #header-group resizes (see the ResizeObserver set up
    // below, in onDocumentLoaded) - it deliberately skips observing the header itself, so without
    // this it goes stale whenever the header's own height changes on its own (nav items wrapping,
    // a late-loading logo image, a font swap reflow, etc.), leaving the mega menu positioned off
    // a height that no longer matches reality.
    const headerGroup = document.querySelector('#header-group');
    if (headerGroup) {
      setHeaderVariable('--header-group-height', calculateHeaderGroupHeight(this, headerGroup));
    }

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Also mirrored on `body`, so sections outside the header can react to it without a `:has()`
   * rule on `body` (see the note in sections/header.liquid)
   * @param {string} state
   */
  #setStickyState(state) {
    this.dataset.stickyState = state;
    document.body.toggleAttribute('data-header-stuck', state === 'active');
  }

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.#setStickyState(isIntersecting ? 'inactive' : 'active');
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    if (this.#scrollTicking) return;
    this.#scrollTicking = true;
    requestAnimationFrame(() => {
      this.#scrollTicking = false;
      this.#handleWindowScrollFrame();
    });
  };

  /**
   * The document's scroll position, clamped to its real scroll range. iOS Safari reports values
   * outside it during its rubber-band overscroll (e.g. -60 at the top), and the bounce back from
   * those reads as a scroll in the opposite direction - at the top, a "scroll down" that hid the
   * header right as it came to rest there.
   * @returns {number}
   */
  get #scrollTop() {
    const scroller = document.scrollingElement;
    if (!scroller) return 0;

    const maxScrollTop = Math.max(scroller.scrollHeight - scroller.clientHeight, 0);
    return Math.min(Math.max(scroller.scrollTop, 0), maxScrollTop);
  }

  #handleWindowScrollFrame = () => {
    const stickyMode = this.getAttribute('sticky');
    const scrollTop = this.#scrollTop;

    if (!this.#offscreen && stickyMode !== 'always') {
      // Keep the reference point current while the header is still in view. Otherwise, after a
      // quick scroll down that ends just as the header leaves the screen, #lastScrollTop is still
      // the old value (e.g. 0), so the first scroll up measures as "down" from there and keeps the
      // header hidden until a second scroll up.
      this.#lastScrollTop = scrollTop;
      return;
    }

    const scrollDelta = scrollTop - this.#lastScrollTop;

    // Ignore sub-threshold deltas (scroll jitter from trackpad/touch momentum) before touching
    // any state, so a pending hide-animation timeout below is left running undisturbed rather
    // than being cancelled and never rescheduled. #lastScrollTop is intentionally left unset
    // here so the delta accumulates until a real direction change occurs.
    if (stickyMode !== 'always' && Math.abs(scrollDelta) < this.#scrollDirectionThreshold) return;

    const isScrollingUp = scrollDelta < 0;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // Header is back at its natural position: ease back to the resting (transparent)
        // look while still pinned - see the [data-settling] rules in sections/header.liquid -
        // then only flip stickyState to 'inactive' (which drops position:sticky) once that
        // transition has had time to finish, mirroring the delayed idle-flip used below for
        // the hide-on-scroll-down animation. Without this delay, position:sticky and the
        // forced-white background were both removed in the same frame the fade started,
        // cutting it off before it could visibly play.
        this.dataset.scrollDirection = 'none';
        this.setAttribute('data-settling', '');

        this.#timeout = setTimeout(() => {
          this.#offscreen = false;
          this.#setStickyState('inactive');
          this.removeAttribute('data-settling');
        }, this.#animationDelay);
      } else {
        // show sticky header when scrolling up
        this.removeAttribute('data-settling');
        this.#setStickyState('active');
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.hasAttribute('data-settling')) {
      // Scrolled down again before the settle above finished: the header is already back at its
      // natural position (in view at the top of the page), so let it scroll away with the page
      // instead of fading it out while it's still on screen.
      this.removeAttribute('data-settling');
      this.dataset.scrollDirection = 'none';
      this.#offscreen = false;
      this.#setStickyState('inactive');
    } else if (this.dataset.stickyState === 'active') {
      this.removeAttribute('data-settling');
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.#setStickyState('idle');
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.removeAttribute('data-settling');
      this.dataset.scrollDirection = 'none';
      this.#setStickyState('idle');
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => {
      setHeaderVariable('--header-group-height', calculateHeaderGroupHeight(header, headerGroup));
    });

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
